import { Check, ChevronsUpDown, Monitor, MonitorOff, RefreshCw, RotateCcw } from 'lucide-react';
import type React from 'react';
import { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import {
    clearCustomIframeSetting,
    getIframeSettingSource,
    setIframePreference,
    shouldAllowIframe,
} from '../config';
import { useAppContext } from '../contexts/AppContext';

interface ClientSelectorProps {
    onConfigChange?: () => void;
}

const ClientSelector: React.FC<ClientSelectorProps> = ({ onConfigChange }) => {
    const [open, setOpen] = useState(false);

    // Get client data and actions from context
    const {
        availableClients,
        selectedClient,
        setSelectedClient,
        refreshClients,
        isRefreshingClients,
    } = useAppContext();

    // Sort clients alphabetically by name
    const sortedClients = useMemo(() => {
        return [...availableClients].sort((a, b) =>
            a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
        );
    }, [availableClients]);

    const handleClientChange = (clientId: string) => {
        const client = availableClients.find((c) => c.id === clientId);
        if (client) {
            setSelectedClient(client);
            setOpen(false);
        }
    };

    const handleIframeToggle = (allowIframe: boolean) => {
        if (!selectedClient?.websiteUrl) return;

        const url = selectedClient.websiteUrl;
        setIframePreference(url, allowIframe);

        // Notify parent component that config changed
        if (onConfigChange) {
            onConfigChange();
        }
    };

    const handleResetToDefault = () => {
        if (!selectedClient?.websiteUrl) return;

        const url = selectedClient.websiteUrl;
        clearCustomIframeSetting(url);

        // Notify parent component that config changed
        if (onConfigChange) {
            onConfigChange();
        }
    };

    const isIframeAllowed = selectedClient?.websiteUrl
        ? shouldAllowIframe(selectedClient.websiteUrl)
        : true;
    const settingSource = selectedClient?.websiteUrl
        ? getIframeSettingSource(selectedClient.websiteUrl)
        : 'none';

    return (
        <>
            {/* Client Selection */}
            <div className="mb-2">
                <div className="flex items-center justify-between mb-1">
                    {/* biome-ignore lint/a11y/noLabelWithoutControl: label is visually associated with the select below */}
                    <label className="text-xs font-medium">🏢 Client Website:</label>
                    <Button
                        onClick={refreshClients}
                        disabled={isRefreshingClients}
                        variant="outline"
                        size="sm"
                        className="h-7 px-2 text-xs"
                    >
                        <RefreshCw
                            className={`h-3 w-3 mr-1 ${isRefreshingClients ? 'animate-spin' : ''}`}
                        />
                        {isRefreshingClients ? 'Refreshing...' : 'Refresh'}
                    </Button>
                </div>
                <Popover open={open} onOpenChange={setOpen}>
                    <PopoverTrigger asChild>
                        <Button
                            variant="outline"
                            role="combobox"
                            aria-expanded={open}
                            className="w-full h-8 justify-between font-normal"
                        >
                            {selectedClient ? (
                                <span className="truncate">
                                    {selectedClient.name} ({selectedClient.location})
                                </span>
                            ) : (
                                <span className="text-muted-foreground">Select a client...</span>
                            )}
                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                    </PopoverTrigger>
                    <PopoverContent
                        className="w-[var(--radix-popover-trigger-width)] p-0 z-[9999]"
                        align="start"
                    >
                        <Command>
                            <CommandInput placeholder="Search workspaces..." />
                            <CommandList>
                                <CommandEmpty>No workspace found.</CommandEmpty>
                                <CommandGroup>
                                    {sortedClients.map((client) => (
                                        <CommandItem
                                            key={client.id}
                                            value={`${client.name} ${client.location} ${client.id}`}
                                            onSelect={() => handleClientChange(client.id)}
                                        >
                                            <Check
                                                className={cn(
                                                    'mr-2 h-4 w-4',
                                                    selectedClient?.id === client.id
                                                        ? 'opacity-100'
                                                        : 'opacity-0'
                                                )}
                                            />
                                            {client.name} ({client.location})
                                        </CommandItem>
                                    ))}
                                </CommandGroup>
                            </CommandList>
                        </Command>
                    </PopoverContent>
                </Popover>
                <div className="text-xs text-gray-500 mt-1">
                    Client list is cached for 1 hour. Use refresh to reload from API endpoint.
                </div>
            </div>

            {/* Client Details */}
            <div className="bg-gray-50 rounded-lg p-2 border">
                <h5 className="text-xs font-medium text-gray-700 mb-1">Client Details</h5>
                <div className="space-y-0.5">
                    <div className="flex justify-between text-xs">
                        <span className="text-gray-500">Name:</span>
                        <span className="font-medium">{selectedClient?.name || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                        <span className="text-gray-500">Domain:</span>
                        <span className="font-medium">{selectedClient?.domain || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                        <span className="text-gray-500">Site Name:</span>
                        <span className="font-medium">{selectedClient?.siteName || 'N/A'}</span>
                    </div>
                    <Separator className="my-0.5" />
                    <div className="text-xs">
                        <span className="text-gray-500">URL:</span>
                        <code className="ml-1 text-xs bg-white px-1 py-0.5 rounded border">
                            {selectedClient?.websiteUrl || 'N/A'}
                        </code>
                    </div>
                    <Separator className="my-0.5" />
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1">
                            {isIframeAllowed ? (
                                <Monitor className="h-3 w-3 text-green-500" />
                            ) : (
                                <MonitorOff className="h-3 w-3 text-orange-500" />
                            )}
                            <span className="text-xs text-gray-500">Allow Iframe:</span>
                            {settingSource === 'default' && (
                                <span className="text-xs text-blue-500 font-medium">(default)</span>
                            )}
                            {settingSource === 'custom' && (
                                <span className="text-xs text-purple-500 font-medium">
                                    (custom)
                                </span>
                            )}
                        </div>
                        <div className="flex items-center gap-1">
                            {settingSource === 'custom' && (
                                <Button
                                    onClick={handleResetToDefault}
                                    size="sm"
                                    variant="ghost"
                                    className="h-5 w-5 p-0"
                                    title="Reset to default"
                                >
                                    <RotateCcw className="h-3 w-3" />
                                </Button>
                            )}
                            <Switch
                                checked={isIframeAllowed}
                                onCheckedChange={handleIframeToggle}
                                className="h-4 w-7"
                            />
                        </div>
                    </div>
                    <div className="text-xs text-gray-400 italic">
                        {isIframeAllowed
                            ? 'Attempts to load site in iframe'
                            : 'Shows fallback placeholder instead of iframe'}
                        {settingSource === 'default' && ' (default behavior)'}
                        {settingSource === 'custom' && ' (custom setting)'}
                    </div>
                </div>
            </div>
        </>
    );
};

export default ClientSelector;
